import styled from '@emotion/styled/macro';
import { LocalizationProvider, PickersDay } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import styles from '@styles/components/DatePicker.module.css';
import { compareAsc, eachDayOfInterval } from 'date-fns';
import es from 'date-fns/locale/es';
import isSameDay from 'date-fns/isSameDay';
import { useEffect, useState } from 'react';
// import { compareAsc } from 'date-fns';

const weekDays = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];

const formatDayOfWeek = (day) => {
	if (day instanceof Date && !Number.isNaN(day.getTime())) {
		return weekDays[day.getDay()];
	}

	return '';
};

export default function DatePicker({ updateDate, initValue = new Date() }) {
	const customEsLocale = {
		...es,
		localize: {
			...es.localize,
			month: (month, { width }) => {
				const monthName = es.localize.month(month, { width });
				return monthName.charAt(0).toUpperCase() + monthName.slice(1);
			},
		},
	};

	const [value, setValue] = useState(initValue);

	const disolveChange = (e) => {
		setValue(null);
		updateDate(null);
	};

	return (
		<>
			<LocalizationProvider adapterLocale={customEsLocale} dateAdapter={AdapterDateFns}>
				<DateCalendar
					dayOfWeekFormatter={formatDayOfWeek}
					showDaysOutsideCurrentMonth
					fixedWeekNumber={6}
					value={value}
					onChange={(newValue) => setValue(newValue)}
					disableFuture
					sx={{
						'& .css-1vooibu-MuiSvgIcon-root': {
							fill: '#EC6C56',
						},
						'& .MuiTypography-root': {
							color: '#000',
							fontWeight: 700,
						},
						'& .MuiPickersDay-root.Mui-selected': {
							backgroundColor: '#EC6C56',
						},
						'& .MuiPickersDay-root.Mui-selected:hover': {
							backgroundColor: '#EC6C56',
						},
						'& .MuiPickersDay-root:focus.Mui-selected': {
							backgroundColor: '#EC6C56',
						},
						'& .MuiPickersDay-dayOutsideMonth': {
							color: '#CDCDCD',
						},
						width: '100%',
						height: '400px',
					}}
				/>
			</LocalizationProvider>
			<div
				style={{
					width: '100%',
					height: '100px',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
				}}>
				<div
					style={{ backgroundColor: '#F4F4F4', color: '#666' }}
					onClick={disolveChange}
					className={styles['button']}>
					Quitar
				</div>
				<div style={{ width: '15px' }} />
				<div
					style={{ backgroundColor: '#EC6C56', color: '#FFF' }}
					onClick={() => updateDate(value)}
					className={styles['button']}>
					Aplicar
				</div>
			</div>
		</>
	);
}

function DatePickerCalendar({
	value,
	setValue,
	disabledPast = false,
	disabledFuture = false,
	daysStyle = {},
	weekdayLabelStyle = {},
	calendarHeaderStyle = {},
	calendarStyle = {},
}) {
	const customEsLocale = {
		...es,
		localize: {
			...es.localize,
			month: (month, { width }) => {
				const monthName = es.localize.month(month, { width });
				return monthName.charAt(0).toUpperCase() + monthName.slice(1);
			},
		},
	};

	const StyledDay = styled(PickersDay, {
		shouldForwardProp: (prop) => prop !== 'selected',
	})(({ theme, selected }) => ({
		...daysStyle,
		...(selected && {
			backgroundColor: '#EC6C56',
			color: theme.palette.common.white,
			'&:hover': {
				backgroundColor: '#EC6C56',
			},
			'&:focus': {
				backgroundColor: '#EC6C56',
			},
		}),
	}));

	const Day = (props) => {
		const { dates = [], day, outsideCurrentMonth, onDaySelect, ...other } = props;

		const isSelected = !props.outsideCurrentMonth && dates.some((date) => isSameDay(date, day));

		return (
			<StyledDay
				{...other}
				outsideCurrentMonth={outsideCurrentMonth}
				day={day}
				selected={isSelected}
				onDaySelect={onDaySelect}
			/>
		);
	};

	return (
		<>
			<LocalizationProvider adapterLocale={customEsLocale} dateAdapter={AdapterDateFns}>
				<DateCalendar
					dayOfWeekFormatter={formatDayOfWeek}
					showDaysOutsideCurrentMonth
					disableFuture={disabledFuture}
					disablePast={disabledPast}
					fixedWeekNumber={6}
					value={value}
					onChange={(newValue) => setValue(newValue)}
					slots={{
						day: Day,
					}}
					slotProps={{
						day: {
							dates: [value],
							onDaySelect: (newValue) => setValue(newValue),
						},
						calendarHeader: {
							sx: {
								'& .MuiPickersCalendarHeader-labelContainer': calendarHeaderStyle,
							},
						},
					}}
					sx={{
						...calendarStyle,
						'& .MuiDayCalendar-weekDayLabel': {
							...weekdayLabelStyle,
							pointerEvents: 'none',
						}, // Apply custom styles to the weekday label
					}}
					views={['day']}
				/>
			</LocalizationProvider>
		</>
	);
}

function DateMultiPicker({ dates, updateDates }) {
	const CustomPickersDay = styled(PickersDay, {
		shouldForwardProp: (prop) => prop !== 'selected',
	})(({ theme, selected }) => ({
		...(selected && {
			backgroundColor: '#EC6C56',
			color: theme.palette.common.white,
			'&:hover': {
				backgroundColor: '#EC6C56',
			},
			'&:focus': {
				backgroundColor: '#EC6C56',
			},
		}),
	}));

	const Day = (props) => {
		const { dates = [], day, outsideCurrentMonth, onDaySelect, ...other } = props;

		const isSelected = !props.outsideCurrentMonth && dates.some((date) => isSameDay(date, day));

		return (
			<CustomPickersDay
				{...other}
				outsideCurrentMonth={outsideCurrentMonth}
				day={day}
				selected={isSelected}
				onDaySelect={onDaySelect}
			/>
		);
	};

	const customEsLocale = {
		...es,
		localize: {
			...es.localize,
			month: (month, { width }) => {
				const monthName = es.localize.month(month, { width });
				return monthName.charAt(0).toUpperCase() + monthName.slice(1);
			},
		},
	};

	const handleClickDay = (clickedDate) => {
		const newDates = dates.filter((date) => !isSameDay(date, clickedDate));

		if (newDates.length === dates.length) {
			newDates.push(clickedDate);
		}

		updateDates(newDates);
	};

	return (
		<>
			<LocalizationProvider adapterLocale={customEsLocale} dateAdapter={AdapterDateFns}>
				<DateCalendar
					dayOfWeekFormatter={formatDayOfWeek}
					fixedWeekNumber={6}
					slots={{
						day: Day,
					}}
					slotProps={{
						day: {
							dates,
							onDaySelect: handleClickDay,
						},
					}}
				/>
			</LocalizationProvider>
		</>
	);
}

function FlexibleDateMultiPicker({
	dates,
	updateDates,
	isRange = false,
	daysStyle = {},
	weekdayLabelStyle = {},
	calendarHeaderStyle = {},
	calendarStyle = {},
}) {
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	const StyledDay = styled(PickersDay, {
		shouldForwardProp: (prop) => prop !== 'selected',
	})(({ theme, selected }) => ({
		...daysStyle,
		...(selected && {
			backgroundColor: '#EC6C56',
			color: theme.palette.common.white,
			'&:hover': {
				backgroundColor: '#EC6C56',
			},
			'&:focus': {
				backgroundColor: '#EC6C56',
			},
		}),
	}));

	const TestDay = (props) => {
		const { dates = [], day, outsideCurrentMonth, onDaySelect, ...other } = props;

		const isSelected = !props.outsideCurrentMonth && dates.some((date) => isSameDay(date, day));

		return (
			<StyledDay
				{...other}
				outsideCurrentMonth={outsideCurrentMonth}
				day={day}
				selected={isSelected}
				onDaySelect={onDaySelect}
			/>
		);
	};

	const customEsLocale = {
		...es,
		localize: {
			...es.localize,
			month: (month, { width }) => {
				const monthName = es.localize.month(month, { width });
				return monthName.charAt(0).toUpperCase() + monthName.slice(1);
			},
		},
	};

	const handleClickSpecificDay = (clickedDate) => {
		const newDates = dates.filter((date) => !isSameDay(date, clickedDate));

		if (newDates.length === dates.length) {
			newDates.push(clickedDate);
		}

		updateDates(newDates);
	};

	const handleClickDayRange = (clickedDate) => {
		if (!startDate) {
			setStartDate(clickedDate);
			updateDates([clickedDate]);
			return;
		}

		if (!endDate) {
			setEndDate(clickedDate);
			let date1 = compareAsc(startDate, clickedDate) === -1 ? startDate : clickedDate;
			let date2 = compareAsc(startDate, clickedDate) === 1 ? startDate : clickedDate;
			const newDates = eachDayOfInterval({
				start: date1,
				end: date2,
			});
			updateDates(newDates);
			return;
		}

		if (startDate && endDate) {
			setStartDate(clickedDate);
			setEndDate(null);
			updateDates([clickedDate]);
		}
	};

	return (
		<LocalizationProvider adapterLocale={customEsLocale} dateAdapter={AdapterDateFns}>
			<DateCalendar
				dayOfWeekFormatter={formatDayOfWeek}
				fixedWeekNumber={6}
				slots={{
					day: TestDay,
				}}
				slotProps={{
					day: {
						dates,
						onDaySelect: isRange ? handleClickDayRange : handleClickSpecificDay,
					},
					calendarHeader: {
						sx: {
							'& .MuiPickersCalendarHeader-labelContainer': calendarHeaderStyle,
						},
					},
				}}
				sx={{
					...calendarStyle,
					'& .MuiDayCalendar-weekDayLabel': {
						...weekdayLabelStyle,
						pointerEvents: 'none',
					}, // Apply custom styles to the weekday label
				}}
				views={['day']}
			/>
		</LocalizationProvider>
	);
}

export { DateMultiPicker, FlexibleDateMultiPicker, DatePickerCalendar };
